import { ApiError } from './client'

/**
 * Ce qu'un refus du serveur dit à quelqu'un — [DECISIONS §406].
 *
 * ── LE DÉFAUT QUE CE MODULE RÉPARE ─────────────────────────────────────────
 *
 * Ben, le 8 septembre 2026, sur la production : « Pourquoi ai-je ce message
 * alors que je suis admin » — une boîte de partage affichant, en anglais,
 * **« Too Many Attempts. »**. C'est la phrase de Laravel pour un 429, rendue
 * telle quelle parce que la fenêtre affiche `body.message` du serveur.
 *
 * Et sur le signalement, l'inverse : un message écrit en dur, **« Vérifiez
 * votre connexion »**, alors que la connexion allait très bien et que le
 * serveur avait dit « quota du jour épuisé ». Le candidat refait le geste, il
 * échoue encore, et il conclut que l'application est cassée.
 *
 * ── LES DEUX RÈGLES ────────────────────────────────────────────────────────
 *
 * **La phrase du serveur passe telle quelle — quand elle est à nous.** Le
 * partage rend « trois par jour », l'archive rend « cette question ne peut pas
 * sortir » : ce sont des explications, et les remplacer par un message
 * générique ferait retaper le même bouton.
 *
 * **Mais une phrase du FRAMEWORK n'est pas une phrase à nous.** Elle est en
 * anglais, elle nomme une mécanique — « attempts », « unauthenticated » — et
 * elle ne dit pas quoi faire. Celles-là se traduisent ici, une fois, plutôt
 * que d'être servies à l'écran.
 */

/** Les phrases que Laravel rend lui-même, et qu'aucun candidat ne doit lire. */
const DU_FRAMEWORK = /^(too many attempts|unauthenticated|server error|unauthorized|forbidden|not found)\.?$/i

/**
 * Le refus, en français, prêt à afficher.
 *
 * @param secours ce qu'on dit quand le serveur n'a rien expliqué — propre à
 *                l'écran appelant, parce que « refaites-le » et « revenez
 *                demain » ne se remplacent pas.
 */
export function refusEnFrancais(erreur: unknown, secours: string): string {
  if (!(erreur instanceof ApiError)) {
    // Ni réponse ni statut : c'est bien le réseau, et le secours de l'écran
    // dit alors la vérité.
    return secours
  }

  const corps = erreur.body
  const message =
    typeof corps === 'object' && corps !== null && 'message' in corps
      ? String(corps.message)
      : ''

  if (message !== '' && !DU_FRAMEWORK.test(message.trim())) {
    return message
  }

  return parLeStatut(erreur.status, secours)
}

/**
 * Ce que le statut seul permet de dire.
 *
 * **Le 429 mérite sa phrase**, et c'est tout l'objet : il ne veut pas dire
 * « ça a raté », il veut dire « pas maintenant ». Un candidat qui le lit sait
 * qu'il n'a rien cassé et qu'il n'a pas à recommencer tout de suite.
 */
function parLeStatut(statut: number, secours: string): string {
  if (statut === 429) {
    return 'Vous êtes allé un peu vite : attendez une minute et refaites-le. Rien n’est perdu.'
  }

  if (statut === 401 || statut === 419) {
    return 'Votre session s’est fermée. Reconnectez-vous, puis refaites le geste.'
  }

  if (statut === 403) {
    return 'Ce geste ne vous est pas ouvert ici.'
  }

  if (statut >= 500) {
    return 'Le serveur n’a pas pu répondre. Réessayez dans un instant.'
  }

  return secours
}
